/* eslint-disable @typescript-eslint/naming-convention */
import React, { memo } from 'react';
import classNames from 'classnames';
import { Button } from '@ui';
import { TOGGLE_EXPAND_ATTR, TOGGLE_TARGET_ID_ATTR } from './DraggableTreeNode';
import { selectIsExpanded } from './useTreeStore';
/**
 * Counter / toggle row rendered on its own virtualized entry between a parent
 * node and its children. This mirrors the original `HierarchicalTree` visual
 * layout where expanding a parent revealed a "▼ N items" row above the child
 * rows. Separating the counter into its own virtualized row keeps every row
 * a uniform height for `react-window`'s `FixedSizeList` while preserving the
 * distinct visual affordance.
 *
 * The counter row intentionally does NOT carry `data-node-id`: that attribute
 * is reserved for real draggable node rows (the DnD handlers in
 * `useTreeDragDrop` walk up via `closest('[data-node-id]')`). Instead, the
 * toggle button carries `data-toggle-expand` + `data-toggle-target-id`, which
 * the container-level click handler reads directly.
 */
function DraggableTreeCounterImpl({ parentNode, store, pluralKey }) {
    var _a, _b;
    const isExpanded = store(selectIsExpanded(parentNode.id));
    const childCount = (_b = (_a = parentNode.children) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0;
    const rowStyle = {
        ['--node-depth']: String(parentNode.depth)
    };
    return (React.createElement("div", { className: classNames('tree-counter', {
            'tree-counter--expanded': isExpanded,
            'tree-counter--collapsed': !isExpanded
        }), style: rowStyle },
        React.createElement(Button, { type: "button", className: "tree-counter__button", "aria-label": isExpanded ? 'Collapse children' : 'Expand children', "aria-expanded": isExpanded, [TOGGLE_EXPAND_ATTR]: 'true', [TOGGLE_TARGET_ID_ATTR]: String(parentNode.id) },
            React.createElement("i", { className: classNames('fa-light tree-counter__icon', {
                    'fa-chevron-down': isExpanded,
                    'fa-chevron-right': !isExpanded
                }) }),
            React.createElement("span", { className: "tree-counter__text" },
                childCount,
                " ",
                pluralKey))));
}
/**
 * Memoised: selective Zustand subscription on `selectIsExpanded` means the
 * counter only re-renders when this parent's expansion state flips.
 */
export const DraggableTreeCounter = memo(DraggableTreeCounterImpl);
